import { memo, useMemo } from 'react';

import { faCheckCircle, faExclamationTriangle, faSpinnerThird, faTimes } from '@fortawesome/pro-solid-svg-icons';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import styled from 'styled-components';

import { Button, ButtonVariants } from '../Button';
import { Icon } from '../Icon';
import { ProgressBar } from '../ProgressBar';
import { ProgressBarType } from '../ProgressBar/constants';
import { StatusBox } from '../StatusBox';
import { ComponentSizes } from '../constants';
import { AIParsingProgressProps, AIParsingStage } from './AIParsingProgressProps';

// Add dayjs plugins
dayjs.extend(duration);
dayjs.extend(relativeTime);

export const AIParsingProgress = memo<AIParsingProgressProps>(
  ({
    className,
    fileId,
    fileName,
    overallProgress,
    currentStage,
    stages,
    estimatedTimeRemaining,
    isComplete = false,
    hasError = false,
    errorMessage,
    size = ComponentSizes.MEDIUM,
    showStageDetails = true,
    showTimeEstimates = true,
    onCancel,
    onRetry,
  }) => {
    const progressBarType = useMemo(() => {
      if (hasError) return ProgressBarType.ERROR;
      if (isComplete) return ProgressBarType.SUCCESS;
      return ProgressBarType.DEFAULT;
    }, [hasError, isComplete]);

    const timeRemainingText = useMemo(() => {
      if (!estimatedTimeRemaining || isComplete || hasError) return '';

      const duration = dayjs.duration(estimatedTimeRemaining, 'seconds');
      if (duration.asMinutes() < 1) return 'Less than a minute remaining';
      if (duration.asHours() < 1) return `${Math.ceil(duration.asMinutes())} minutes remaining`;
      return `${Math.ceil(duration.asHours())} hours remaining`;
    }, [estimatedTimeRemaining, isComplete, hasError]);

    const currentStageData = useMemo(() => {
      return stages.find((stage) => stage.id === currentStage);
    }, [stages, currentStage]);

    const completedStages = useMemo(() => {
      return stages.filter((stage) => stage.status === 'completed').length;
    }, [stages]);

    const renderStageIcon = (stage: AIParsingStage) => {
      switch (stage.status) {
        case 'completed':
          return <StyledIcon icon={faCheckCircle} $status="completed" />;
        case 'in_progress':
          return <StyledIcon icon={faSpinnerThird} spin $status="in_progress" />;
        case 'error':
          return <StyledIcon icon={faExclamationTriangle} $status="error" />;
        default:
          return <StyledIcon icon={faCheckCircle} $status="pending" />;
      }
    };

    const formatStageDuration = (stage: AIParsingStage) => {
      if (!stage.startTime) return '';
      const endTime = stage.endTime || new Date();
      const duration = dayjs(endTime).diff(dayjs(stage.startTime), 'seconds');
      if (duration < 60) return `${duration}s`;
      return `${Math.floor(duration / 60)}m ${duration % 60}s`;
    };

    return (
      <StyledContainer className={className} $size={size}>
        <StyledHeader>
          <StyledFileInfo>
            <StyledFileName>{fileName || `File ${fileId}`}</StyledFileName>
            <StyledProcessingText>
              {hasError ? 'AI Processing Failed' : isComplete ? 'AI Processing Complete' : 'AI Processing in Progress'}
            </StyledProcessingText>
          </StyledFileInfo>

          {!isComplete && !hasError && onCancel && (
            <StyledCancelButton variant={ButtonVariants.SECONDARY} size={ComponentSizes.SMALL} onClick={onCancel} aria-label="Cancel AI processing">
              <Icon icon={faTimes} />
            </StyledCancelButton>
          )}
        </StyledHeader>

        <StyledProgressSection>
          <StyledProgressBar value={overallProgress} size={size} subType={progressBarType} labelResolver={(value) => `${Math.floor(value || 0)}%`} />

          {showTimeEstimates && timeRemainingText && <StyledTimeRemaining>{timeRemainingText}</StyledTimeRemaining>}
        </StyledProgressSection>

        {currentStageData && (
          <StyledCurrentStage>
            {renderStageIcon(currentStageData)}
            <StyledStageInfo>
              <StyledStageName>{currentStageData.name}</StyledStageName>
              {currentStageData.description && <StyledStageDescription>{currentStageData.description}</StyledStageDescription>}
            </StyledStageInfo>
            {currentStageData.progress !== undefined && <StyledStageProgress>{Math.floor(currentStageData.progress)}%</StyledStageProgress>}
          </StyledCurrentStage>
        )}

        {showStageDetails && (
          <StyledStagesList>
            {stages.map((stage) => (
              <StyledStageItem key={stage.id} $status={stage.status}>
                {renderStageIcon(stage)}
                <StyledStageItemInfo>
                  <StyledStageItemName>{stage.name}</StyledStageItemName>
                  {stage.status === 'error' && stage.errorMessage && <StyledStageError>{stage.errorMessage}</StyledStageError>}
                  {(stage.status === 'completed' || stage.status === 'error') && <StyledStageDuration>{formatStageDuration(stage)}</StyledStageDuration>}
                </StyledStageItemInfo>
              </StyledStageItem>
            ))}
          </StyledStagesList>
        )}

        {hasError && (
          <StyledErrorSection>
            <StatusBox status="error">{errorMessage || 'An error occurred during AI processing'}</StatusBox>
            {onRetry && (
              <StyledRetryButton variant={ButtonVariants.PRIMARY} size={ComponentSizes.SMALL} onClick={onRetry}>
                Retry Processing
              </StyledRetryButton>
            )}
          </StyledErrorSection>
        )}

        {isComplete && (
          <StyledSuccessSection>
            <StatusBox status="success">
              AI processing completed successfully ({completedStages}/{stages.length} stages)
            </StatusBox>
          </StyledSuccessSection>
        )}
      </StyledContainer>
    );
  },
);

AIParsingProgress.displayName = 'AIParsingProgress';

const StyledContainer = styled.div<{ $size: ComponentSizes }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.spacing20};
  padding: ${({ theme }) => theme.space.spacing20};
  background-color: ${({ theme }) => theme.colors.palette.white};
  border: 1px solid ${({ theme }) => theme.colors.palette.grayscale[4]};
  border-radius: 4px;
  font-size: ${({ theme, $size }) => ($size === ComponentSizes.SMALL ? theme.fontSizes.footnote : theme.fontSizes.body)};
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${({ theme }) => theme.space.spacing20};
`;

const StyledFileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const StyledFileName = styled.div`
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  font-size: ${({ theme }) => theme.fontSizes.heading2};
  color: ${({ theme }) => theme.colors.palette.grayscale[9]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledProcessingText = styled.div`
  color: ${({ theme }) => theme.colors.palette.grayscale[7]};
  font-size: ${({ theme }) => theme.fontSizes.body};
  margin-top: ${({ theme }) => theme.space.spacing10};
`;

const StyledCancelButton = styled(Button)`
  flex-shrink: 0;
`;

const StyledProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.spacing10};
`;

const StyledProgressBar = styled(ProgressBar)``;

const StyledTimeRemaining = styled.div`
  color: ${({ theme }) => theme.colors.palette.grayscale[6]};
  font-size: ${({ theme }) => theme.fontSizes.body};
  text-align: center;
`;

const StyledCurrentStage = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.spacing10};
  padding: ${({ theme }) => theme.space.spacing10};
  background-color: ${({ theme }) => theme.colors.palette.grayscale[1]};
  border-radius: 4px;
`;

const StyledStageInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const StyledStageName = styled.div`
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  color: ${({ theme }) => theme.colors.palette.grayscale[9]};
`;

const StyledStageDescription = styled.div`
  color: ${({ theme }) => theme.colors.palette.grayscale[6]};
  font-size: ${({ theme }) => theme.fontSizes.footnote};
  margin-top: ${({ theme }) => theme.space.spacing10};
`;

const StyledStageProgress = styled.div`
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  color: ${({ theme }) => theme.colors.primary};
`;

const StyledStagesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.spacing10};
`;

const StyledStageItem = styled.div<{ $status: string }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.spacing10};
  padding: ${({ theme }) => theme.space.spacing10};
  opacity: ${({ $status }) => ($status === 'pending' ? 0.6 : 1)};
`;

const StyledStageItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const StyledStageItemName = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.footnote};
  color: ${({ theme }) => theme.colors.palette.grayscale[8]};
`;

const StyledStageError = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.fontSizes.footnote};
  margin-top: ${({ theme }) => theme.space.spacing10};
`;

const StyledStageDuration = styled.div`
  color: ${({ theme }) => theme.colors.palette.grayscale[6]};
  font-size: ${({ theme }) => theme.fontSizes.footnote};
  margin-top: ${({ theme }) => theme.space.spacing10};
`;

const StyledIcon = styled(Icon)<{ $status: string }>`
  color: ${({ theme, $status }) => {
    switch ($status) {
      case 'completed':
        return theme.colors.success;
      case 'in_progress':
        return theme.colors.primary;
      case 'error':
        return theme.colors.error;
      default:
        return theme.colors.palette.grayscale[5];
    }
  }};
  flex-shrink: 0;
`;

const StyledErrorSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.spacing10};
`;

const StyledRetryButton = styled(Button)`
  align-self: flex-start;
`;

const StyledSuccessSection = styled.div``;
